import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import api from '@/lib/api';
import { toast } from 'sonner';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import { getPublicSiteUrl, stripHtmlForMeta, joinSitePath, absoluteImageUrl } from '@/lib/seo';
import { buildPathForLocale, getLocaleFromPathname } from '@/lib/intl-seo';
import { renderSocialMetaTags } from '@/components/seo/SocialMetaTags';
import { breadcrumbListJsonLd, restaurantJsonLd, jsonLdScript } from '@/lib/schema-org';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, Phone, Globe, Star, Clock, ChefHat, AlertCircle, 
  ArrowLeft, Heart, Share2, Navigation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RestaurantDetailPage = () => {
  const { t } = useTranslation();
  const { id, lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const langCode = lang || getLocaleFromPathname(location.pathname);
  const site = getPublicSiteUrl();
  const pageUrl = site && id ? joinSitePath(site, buildPathForLocale(langCode, ['restaurants', id])) : '';
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrderWarning, setShowOrderWarning] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  useEffect(() => {
    const fetchRestaurant = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/restaurants/${id}`);
        if (res.ok) {
          const data = await res.json();
          setRestaurant(data);
        }
      } catch (error) {
        console.error('Failed to fetch restaurant:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurant();
  }, [id]);

  useEffect(() => {
    const checkSaved = async () => {
      if (!user || !id) {
        setIsSaved(false);
        return;
      }
      try {
        const res = await api.get(`/saved-restaurants/check/${id}`);
        setIsSaved(!!res.data.is_saved);
      } catch {
        setIsSaved(false);
      }
    };
    checkSaved();
  }, [user, id]);

  const handleSave = useCallback(async () => {
    if (!user) {
      toast.error(t('restaurantsPage.loginToSave', 'Kaydetmek için giriş yapmalısınız'));
      navigate(`${buildPathForLocale(langCode, ['login'])}?redirect=${encodeURIComponent(buildPathForLocale(langCode, ['restaurants', id]))}`);
      return;
    }
    setSaveLoading(true);
    try {
      const res = await api.post('/saved-restaurants/toggle', { restaurant_id: id });
      const saved = !!res.data.is_saved;
      setIsSaved(saved);
      if (saved) {
        toast.success(t('restaurantsPage.savedSuccess', 'Restoran kaydedildi'), {
          description: t('restaurantsPage.savedWhere', 'Hesabım → Kaydettiğim Restoranlar'),
          action: {
            label: t('restaurantsPage.viewSaved', 'Listeye git'),
            onClick: () => navigate(buildPathForLocale(langCode, ['account', 'saved-restaurants'])),
          },
        });
      } else {
        toast.success(t('restaurantsPage.removedFromSaved', 'Kayıttan kaldırıldı'));
      }
    } catch (e) {
      toast.error(t('restaurantsPage.saveError', 'Kaydetme başarısız'));
    } finally {
      setSaveLoading(false);
    }
  }, [user, id, langCode, navigate, t]);

  const handleShare = useCallback(async () => {
    const shareUrl =
      pageUrl || (typeof window !== 'undefined' ? window.location.href : '');
    if (!shareUrl) return;
    setShareLoading(true);
    try {
      const shareData = {
        title: restaurant?.name || 'Turkishemarket',
        text: restaurant?.name || '',
        url: shareUrl,
      };
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t('restaurantsPage.linkCopied', 'Link panoya kopyalandı'));
      } else {
        window.prompt(t('restaurantsPage.copyLink', 'Linki kopyalayın:'), shareUrl);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error(t('restaurantsPage.shareError', 'Paylaşım başarısız'));
      }
    } finally {
      setShareLoading(false);
    }
  }, [pageUrl, restaurant, t]);

  const getCountryFlag = (countryCode) => {
    if (!countryCode) return '🍽️';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const canOrder = restaurant?.is_active && restaurant?.status !== 'pending_claim';

  const handleOrderClick = () => {
    if (!canOrder) {
      setShowOrderWarning(true);
    }
  };

  const metaDesc = useMemo(() => {
    if (!restaurant) return '';
    const fromDesc = stripHtmlForMeta(restaurant.description, 160);
    if (fromDesc) return fromDesc;
    const parts = [restaurant.name, restaurant.city, restaurant.country].filter(Boolean);
    return parts.join(' — ').slice(0, 160);
  }, [restaurant]);

  const breadcrumbUi = useMemo(() => {
    if (!restaurant) return [];
    return [
      { label: t('common.home', 'Ana Sayfa'), to: buildPathForLocale(langCode, []) },
      { label: t('restaurantsPage.title', 'Restoranlar'), to: buildPathForLocale(langCode, ['restaurants']) },
      { label: restaurant.name, to: null },
    ];
  }, [t, langCode, restaurant]);

  const breadcrumbLd = useMemo(() => {
    if (!site || !pageUrl || !restaurant) return '';
    return jsonLdScript(
      breadcrumbListJsonLd([
        { name: t('common.home', 'Ana Sayfa'), item: joinSitePath(site, buildPathForLocale(langCode, [])) },
        {
          name: t('restaurantsPage.title', 'Restoranlar'),
          item: joinSitePath(site, buildPathForLocale(langCode, ['restaurants'])),
        },
        { name: restaurant.name, item: pageUrl },
      ]),
    );
  }, [site, pageUrl, langCode, t, restaurant]);

  const restaurantLd = useMemo(
    () =>
      pageUrl && restaurant
        ? jsonLdScript(restaurantJsonLd({ restaurant, pageUrl, siteBase: site }))
        : '',
    [restaurant, pageUrl, site],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <ChefHat className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-medium text-gray-600">{t('restaurantsPage.noRestaurants', 'Restoran bulunamadı')}</h2>
        <Link to={buildPathForLocale(langCode, ['restaurants'])}>
          <Button variant="link" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('restaurantsPage.backToRestaurants', 'Restoranlara Dön')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-seo-ready="true">
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{`${restaurant.name} | Turkish E-Market`}</title>
          <meta name="description" content={metaDesc} />
          {pageUrl ? <link rel="canonical" href={pageUrl} /> : null}
          {renderSocialMetaTags({
            title: `${restaurant.name} | Turkish E-Market`,
            description: metaDesc,
            url: pageUrl,
            image: absoluteImageUrl(site, restaurant.images?.[0]) || (site ? `${site}/logo.png` : ''),
            type: 'website',
            locale: langCode,
          })}
          {breadcrumbLd ? <script type="application/ld+json">{breadcrumbLd}</script> : null}
          {restaurantLd ? <script type="application/ld+json">{restaurantLd}</script> : null}
        </Helmet>
        <div className="container mx-auto max-w-6xl px-4 pt-3">
          <PageBreadcrumb items={breadcrumbUi} />
        </div>
        {/* Order Warning Modal */}
        {showOrderWarning && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="max-w-lg w-full">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="w-10 h-10 text-amber-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('restaurantsPage.orderNotAvailable', 'Henüz Sipariş Alınamıyor')}
                </h2>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {t('restaurantsPage.notJoinedFamily', 'Bu restoran henüz sizler için seçtiğimiz Türk restoranları ailesine katılmamış.')}
                </p>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800 font-medium">
                    🍽️ {t('restaurantsPage.familyWishes', 'Turkishemarket Ailesi afiyetler diler')}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Button 
                    onClick={() => setShowOrderWarning(false)}
                    className="w-full"
                  >
                    {t('common.understood', 'Anladım')}
                  </Button>
                  
                  <Link to={buildPathForLocale(langCode, ['restaurants'])} className="block">
                    <Button variant="outline" className="w-full">
                      {t('restaurantsPage.exploreOthers', 'Diğer Restoranları Keşfet')}
                    </Button>
                  </Link>
                </div>
                
                <p className="text-xs text-gray-500 mt-6">
                  {t('restaurantsPage.ownerNote', 'Restoran sahibiyseniz, üyelik sürecini tamamlayarak restoranınızı aktif hale getirebilirsiniz.')}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Back Button */}
        <div className="bg-white border-b">
          <div className="container mx-auto max-w-6xl px-4 py-3">
            <Link to={buildPathForLocale(langCode, ['restaurants'])} className="inline-flex items-center text-gray-600 hover:text-red-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('restaurantsPage.backToRestaurants', 'Restoranlara Dön')}
            </Link>
          </div>
        </div>

        {/* Hero Image */}
        <div className="relative h-64 md:h-96 bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center">
          {restaurant.images?.[0] ? (
            <img 
              src={restaurant.images[0]} 
              alt={restaurant.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <ChefHat className="w-32 h-32 text-red-300" />
          )}
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          
          {/* Status Badge */}
          {!canOrder && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-amber-500 text-white px-4 py-2 text-sm">
                <AlertCircle className="w-4 h-4 mr-2" />
                {t('restaurantsPage.comingSoonActive', 'Yakında Aktif')}
              </Badge>
            </div>
          )}
          
          {/* Restaurant Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{getCountryFlag(restaurant.country_code)}</span>
                <span className="text-sm opacity-80">{restaurant.country}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{restaurant.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span>{restaurant.rating?.toFixed(1) || '4.5'}</span>
                  <span className="opacity-70">({restaurant.review_count || 0} {t('restaurantsPage.reviews', 'değerlendirme')})</span>
                </div>
                <span>{restaurant.price_range || '€€'}</span>
                <span>{restaurant.cuisine_type || 'Turkish'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto max-w-6xl px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Features */}
              {restaurant.features?.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2">
                      {restaurant.features.map((feature, idx) => (
                        <Badge key={idx} variant="secondary" className="px-3 py-1">
                          {feature === 'Halal' ? `🥗 ${t('restaurantsPage.halal', 'Helal')}` : feature}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Opening Hours */}
              {restaurant.opening_hours && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-red-600" />
                      {t('restaurantsPage.openingHours', 'Çalışma Saatleri')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(restaurant.opening_hours).map(([day, hours]) => (
                        <div key={day} className="flex justify-between py-1 border-b last:border-0">
                          <span className="capitalize text-gray-600">
                            {t(`restaurantsPage.days.${day}`, day)}
                          </span>
                          <span className="font-medium">{hours}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Not Active Warning */}
              {!canOrder && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-amber-800 mb-1">{t('restaurantsPage.notActiveYet', 'Restoran Henüz Aktif Değil')}</h3>
                        <p className="text-amber-700 text-sm">
                          {t('restaurantsPage.notActiveDesc', 'Bu restoran henüz Turkishemarket ailesine katılmamış. Restoran sahibiyseniz, üyelik sürecini tamamlayarak restoranınızı aktif hale getirebilirsiniz.')}
                        </p>
                        <Link to={`${buildPathForLocale(langCode, ['seller-register'])}?category=restaurants`}>
                          <Button variant="outline" size="sm" className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-100">
                            {t('restaurantsPage.becomePartner', 'Restoran Ortağı Ol')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('restaurantsPage.contact', 'İletişim')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm">{restaurant.address}</p>
                      <p className="text-sm text-gray-500">{restaurant.city}, {restaurant.country}</p>
                    </div>
                  </div>
                  
                  {restaurant.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <a href={`tel:${restaurant.phone}`} className="text-sm hover:text-red-600">
                        {restaurant.phone}
                      </a>
                    </div>
                  )}
                  
                  {restaurant.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-gray-400" />
                      <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-red-600 truncate">
                        {restaurant.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                  
                  {restaurant.maps_url && (
                    <a href={restaurant.maps_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full mt-2">
                        <Navigation className="w-4 h-4 mr-2" />
                        {t('restaurantsPage.showOnMap', 'Haritada Göster')}
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>

              {/* Order Button */}
              <Button 
                className={`w-full h-14 text-lg ${canOrder ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400'}`}
                onClick={handleOrderClick}
              >
                {canOrder ? t('restaurantsPage.placeOrder', 'Sipariş Ver') : t('restaurantsPage.orderNotAvailableBtn', 'Sipariş Alınamıyor')}
              </Button>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className={`flex-1 ${isSaved ? 'border-red-300 text-red-600' : ''}`}
                  onClick={handleSave}
                  disabled={saveLoading}
                >
                  <Heart className={`w-4 h-4 mr-2 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} />
                  {saveLoading
                    ? '...'
                    : isSaved
                      ? t('restaurantsPage.saved', 'Kaydedildi')
                      : t('common.save', 'Kaydet')}
                </Button>
                <Button variant="outline" className="flex-1" onClick={handleShare} disabled={shareLoading}>
                  <Share2 className="w-4 h-4 mr-2" />
                  {t('common.share', 'Paylaş')}
                </Button>
              </div>
              {user && (
                <p className="text-xs text-center text-muted-foreground">
                  {t('restaurantsPage.savedHint', 'Kaydettiğiniz restoranlar')}{' '}
                  <Link to={buildPathForLocale(langCode, ['account', 'saved-restaurants'])} className="text-red-600 hover:underline">
                    {t('restaurantsPage.savedRestaurantsTitle', 'Kaydettiğim Restoranlar')}
                  </Link>
                  {' '}{t('restaurantsPage.savedHintSuffix', 'sayfasında görünür.')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default RestaurantDetailPage;
